import type { CredentialFieldSchema } from '@/types/credentialSchemas'

/** A field draft: string = new value, null = Clear (delete the stored key so
 *  the default/unset applies). Absent from the map = untouched. */
export type ConfigDrafts = Record<string, string | null>

export function isDirty(stored: string | undefined, draft: string | null | undefined): boolean {
  if (draft === undefined) return false
  if (draft === null) return stored !== undefined
  return draft !== stored // '' !== undefined: explicit-none differs from unset
}

/** Store a draft, dropping it when it matches the stored baseline so an edit
 *  that round-trips back to the original counts as untouched. Exception: an
 *  explicit "" on a dynamic-options field with nothing stored stays a draft —
 *  that's the reachable "send none over the adapter default" state. */
export function withDraft(
  drafts: ConfigDrafts,
  field: CredentialFieldSchema,
  stored: string | undefined,
  value: string | null,
): ConfigDrafts {
  const next = { ...drafts }
  const untouched =
    value === null
      ? stored === undefined
      : stored !== undefined
        ? value === stored
        : value === '' && field.dynamic_options === ''
  if (untouched) delete next[field.key]
  else next[field.key] = value
  return next
}

export interface EntryBodyResult {
  body: {
    service: string
    name: string
    api_key?: string
    config?: Record<string, string | null>
    secrets?: Record<string, string | null>
  }
  changedFields: CredentialFieldSchema[]
  changedCount: number
}

/** Secondary secret drafts: string = replace, null = staged Clear,
 *  absent = keep stored. Mirrors ConfigDrafts semantics. */
export type SecretsDrafts = Record<string, string | null>

export function isSecondaryDirty(
  storedSet: boolean,
  draft: string | null | undefined,
): boolean {
  if (draft === undefined) return false
  if (draft === null) return storedSet // Clear only means something if set
  return draft.trim() !== ''
}

/** The merge-on-save contract, in one place: post ONLY dirty schema fields,
 *  null for clears, blank secret omitted entirely. */
export function buildEntryBody(args: {
  service: string
  name: string
  configFields: CredentialFieldSchema[]
  storedConfig: Record<string, string> | null
  drafts: ConfigDrafts
  secretDraft: string
  secondaryFields?: CredentialFieldSchema[]
  secretsDrafts?: SecretsDrafts
  storedSecretsSet?: Record<string, boolean>
}): EntryBodyResult {
  const {
    service, name, configFields, storedConfig, drafts, secretDraft,
    secondaryFields = [], secretsDrafts = {}, storedSecretsSet = {},
  } = args
  const changedFields = configFields.filter((f) => isDirty(storedConfig?.[f.key], drafts[f.key]))
  const dirtySecondary = secondaryFields.filter((f) =>
    isSecondaryDirty(storedSecretsSet[f.key] ?? false, secretsDrafts[f.key]),
  )
  const body: EntryBodyResult['body'] = { service, name }
  const secret = secretDraft.trim()
  if (secret) body.api_key = secret
  if (changedFields.length > 0) {
    body.config = Object.fromEntries(
      changedFields.map((f) => [f.key, drafts[f.key] as string | null]),
    )
  }
  if (dirtySecondary.length > 0) {
    body.secrets = Object.fromEntries(
      dirtySecondary.map((f) => {
        const d = secretsDrafts[f.key]
        return [f.key, d === null ? null : (d as string).trim()]
      }),
    )
  }
  return {
    body,
    changedFields: [...changedFields, ...dirtySecondary],
    changedCount: changedFields.length + dirtySecondary.length + (secret ? 1 : 0),
  }
}

export function describeChange(
  field: CredentialFieldSchema,
  stored: string | undefined,
  draft: string | null,
): string {
  if (draft === null) {
    return field.default_value
      ? `will be cleared — default "${field.default_value}" applies`
      : 'will be cleared'
  }
  if (draft === '') return 'set to none'
  return stored !== undefined ? `"${stored}" → "${draft}"` : `set to "${draft}"`
}
